"use client"

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import type { PublicLowStock } from "@/lib/inventory/publicLowStock"

/**
 * «¡Quedan pocas!» del kiosko y del menú en línea: quién está en pocas, sin
 * números (ver lib/inventory/publicLowStock.ts y /api/menu/low-stock).
 *
 * Contexto y no props: la etiqueta la leen la tarjeta del plato Y los selectores
 * de sabor (modal clásico, flujo guiado), varios niveles abajo y montados desde
 * lugares distintos. Sin proveedor, `useLowStock` responde «no» a todo: cualquier
 * superficie que no lo monte queda exactamente como estaba.
 *
 * Sondeo cada 60 s (la respuesta va cacheada 20 s en el CDN) y al volver a la
 * pestaña. Un fallo conserva la última lista.
 */

type LowStockApi = {
  item: (itemId: string) => boolean
  option: (optionId: string) => boolean
}

const NONE: LowStockApi = { item: () => false, option: () => false }
const LowStockCtx = createContext<LowStockApi>(NONE)

export const useLowStock = () => useContext(LowStockCtx)

const POLL_MS = 60_000

export function LowStockProvider({
  locationId,
  enabled = true,
  children,
}: {
  locationId: string | null | undefined
  enabled?: boolean
  children: ReactNode
}) {
  const [data, setData] = useState<PublicLowStock>({ items: [], options: [] })

  useEffect(() => {
    if (!enabled || !locationId) return
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(`/api/menu/low-stock?locationId=${encodeURIComponent(locationId)}`)
        if (!res.ok) return
        const body = (await res.json()) as Partial<PublicLowStock>
        if (cancelled || !Array.isArray(body.items) || !Array.isArray(body.options)) return
        setData({ items: body.items, options: body.options })
      } catch {
        // Hipo de red: se conserva la última lista.
      }
    }
    void load()
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void load()
    }, POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === "visible") void load()
    }
    document.addEventListener("visibilitychange", onVisible)
    return () => {
      cancelled = true
      clearInterval(id)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [enabled, locationId])

  const api = useMemo<LowStockApi>(() => {
    const items = new Set(data.items)
    const options = new Set(data.options)
    return { item: (id) => items.has(id), option: (id) => options.has(id) }
  }, [data])

  return <LowStockCtx.Provider value={api}>{children}</LowStockCtx.Provider>
}
